import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useSearchParams} from "react-router-dom";
import { getUserFromToken , setAuthContext, verifyAndRefreshUserToken,logoutUser, getLob } from "../../services/authenticationService";
import { getSalesLensUrl, fetchSaleLensConfig, getAllAccessibleRoutes, getPortalRoleConfig, getMetaDataConfig, fetchDMSConfig, doFinalConfigSubmit, fetchPricingAtLogin, fetchPortalClientConfig } from "../../services/uiConfigService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { setPortalConfig } from "../../stateManagement/actions/portalConfigActions";
import store from "../../stateManagement/store";
import { Loader } from "../loader/Loader";
import { fetchProducts, prepareCatalogue ,fetchRouteIdNameMapping} from "../../services/catalogueService";
import { fetchAndStoreClientConfig, getConfigData } from "../../services/configurationService";
import { setRealTimeStockDataInDb } from "../../features/stocks/RealTimeStock/RealTimeStockUtils";
import { getCompanyDesignations, getDataByModel, getDataSyncManagerConfig, getOnboardingConfig, IOnboardingData, isDeltaEnabled, isProductionMovementCompleted, isStandardLob } from "../../services/userOnboardService/userOnboardService";
import { Fab, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ProductionMovementFloaterIcon from "./productionMovementFloaterIcon";
import { ChannelkartNetworkGet, ChannelkartNetworkPut } from "../../services/networkService";
import { initializePushNotifications } from "../../services/pushNotificationService";
import { clearAllDataFromLocalDb, getDataFromLocalDb, insertDataIntoLocalDB } from "../../services/dbService";
import { fetchOutletDetails, fetchSiteWarehouseMapping, fetchSkuReplacementMapping } from "../../features/createSecondaryOrder/CreateSecondaryOrderService";
import { OutletDetails } from "../../features/createSecondaryOrder/models";
import { addMinutesToUTCTimestamp, getCurrentUTCTimestamp, isTimestampExpired } from "../../utils/calenderUtils";
import { completeDayEnd, getDayEndStatusFromSession, setDayEndStatusInSession } from "../dms_common/DayEndUtils";

export type DataSyncManagerType = {
  name: string;
  status: string;
  updatedTime: string;
  expiryTime: string;
  category: string;
}

export type DataSyncManagerConfig = {
  indexDb: string;
  model: string;
  syncTimeInMinutes: number;
}

export function DashboardAuth() {
    const clientConfigRef = useRef<any[]>([]);
    const onboardingDataRef = useRef<IOnboardingData>();
    const currentRoute = (useLocation().pathname);
    const { showProductionMovementSuccessPopup } = (useLocation().state || {}) as { showProductionMovementSuccessPopup: boolean | undefined };
    const navigate = useNavigate();
    const [loadChildRoute,setLoadChildRoute] = useState<boolean>(false);
    const [searchParams , setSearchParams] = useSearchParams();
    
    const token = searchParams.get('token');
    const error = searchParams.get('error');

    useEffect(() => {
        async function getAndSetClientConfig(){
            const clientConfig = await getMetaDataConfig("clientconfig");
            clientConfigRef.current = clientConfig;
            //will execute only when dayEnd config is enabled
            await setDayEndStatusInSession();
        }
        getAndSetClientConfig()
    },[])

    useEffect(() => {
      const dataSync = async () => {
        try {
          // Get portal configuration
          let portalConfig;
          if (clientConfigRef.current?.length > 0) {
            portalConfig = clientConfigRef.current.find(
              (config: any) => config.domainType === "portal_configuration"
            );
          } else {
            portalConfig = await getMetaDataConfig("clientconfig", "portal_configuration");
          }

          // Check if delta sync is enabled
          if (!portalConfig || !isDeltaEnabled(portalConfig)) {
            return;
          }

          const dataSyncManagerConfig = getDataSyncManagerConfig(portalConfig);
          if (!dataSyncManagerConfig?.length) {
            return;
          }

          const modelsToSync = dataSyncManagerConfig.map(config => config.model);
          if (!modelsToSync.length) {
            return;
          }

          const authContextUser = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user;
          const channelDivision = authContextUser?.channelDivision;
          const currentDesignation = authContextUser?.designation || [];
          const skipsDeltaForCompanyDesignations = getCompanyDesignations(portalConfig);

          // Normalize currentDesignation to an array
          const designationList = Array.isArray(currentDesignation)
            ? currentDesignation
            : [currentDesignation];

          // Check if any relevant designation is present
          const isCompany = skipsDeltaForCompanyDesignations?.some((designation: string) =>
            designationList?.includes(designation)
          );
          if(channelDivision && !isCompany) await performDataSync(dataSyncManagerConfig);

        } catch (error) {
          console.error("Data sync failed:", error);
        }
      };

      const performDataSync = async (
        dataSyncManagerConfig: DataSyncManagerConfig[],
      ) => {
        const currentTime = getCurrentUTCTimestamp();
        const lastDataSyncTime = localStorage.getItem("lastDataSyncTime") || undefined;

        if (!lastDataSyncTime) {
          // First time sync - clear all data and sync from fixed timestamp
          await performInitialSync(dataSyncManagerConfig, currentTime);
        } else {
          // Incremental sync - check for expired data and sync accordingly
          await performIncrementalSync(dataSyncManagerConfig, lastDataSyncTime);
        }
      };

      const performInitialSync = async (
        dataSyncManagerConfig: DataSyncManagerConfig[],
        currentTime: string
      ) => {
        
        await clearAllDataFromLocalDb("DataSyncManager");

        const initialDataSyncManager: DataSyncManagerType[] = await Promise.all(
          dataSyncManagerConfig?.map(async (config) => {
            try {
              let dataByModel = await getDataByModel(config?.model);
              if(dataByModel?.length !== 0) {
                await insertDataIntoLocalDB(config?.indexDb, dataByModel);
              }
              let dataSyncModel: DataSyncManagerType = {
                name: config?.indexDb,
                status: "success",
                updatedTime: currentTime,
                expiryTime: addMinutesToUTCTimestamp(currentTime, config?.syncTimeInMinutes),
                category: ""
              };
              return dataSyncModel;
            } catch (error) {
              let dataSyncModel: DataSyncManagerType = {
                name: config.indexDb,
                status: "error",
                updatedTime: currentTime,
                expiryTime: addMinutesToUTCTimestamp(currentTime, config.syncTimeInMinutes * -1),
                category: ""
              };
              return dataSyncModel;
            }
          })
        );
          
          await insertDataIntoLocalDB("DataSyncManager", initialDataSyncManager);
          localStorage.setItem("lastDataSyncTime", new Date().toISOString());
      };

      const performIncrementalSync = async (
        dataSyncManagerConfig: DataSyncManagerConfig[],
        lastSyncTime: string
      ) => {

        const dataSyncManagerDb = await getDataFromLocalDb("DataSyncManager");
        const existingDataSyncEntries: DataSyncManagerType[] = dataSyncManagerDb?.data || [];

        if (!existingDataSyncEntries.length) {
          await performInitialSync(dataSyncManagerConfig, lastSyncTime);
          return;
        }

        const updatedDataSyncManager: DataSyncManagerType[] = [];
        const expiredModels: string[] = [];

        // Check for expired entries and models that need syncing
        for (const config of dataSyncManagerConfig) {
          const existingEntry = existingDataSyncEntries.find(entry => entry.name === config.indexDb);
          
          if (!existingEntry) {
            // New model config - add it
            const newEntry: DataSyncManagerType = {
              name: config.indexDb,
              status: "pending",
              updatedTime: lastSyncTime,
              expiryTime: addMinutesToUTCTimestamp(lastSyncTime, config.syncTimeInMinutes),
              category: ""
            };
            updatedDataSyncManager.push(newEntry);
            expiredModels.push(config.model);
          } else if (isTimestampExpired(existingEntry.expiryTime)) {
            // Expired entry - mark for sync
            const updatedEntry: DataSyncManagerType = {
              ...existingEntry,
              status: "pending",
              updatedTime: lastSyncTime,
              expiryTime: addMinutesToUTCTimestamp(lastSyncTime, config.syncTimeInMinutes)
            };
            updatedDataSyncManager.push(updatedEntry);
            expiredModels.push(config.model);
          } else {
            // Still valid - keep as is
            updatedDataSyncManager.push(existingEntry);
          }
        }

        if (expiredModels.length === 0) {
          return;
        }

        try {
          const response = await ChannelkartNetworkGet(`/v1/delta/changes?from=${lastSyncTime}`);
          
          const updatedFeatures = response?.features?.filter((feature: any) => 
            expiredModels.includes(feature.name)
          ) || [];

          // Update sync status based on response
          for (const entry of updatedDataSyncManager) {
            if (entry.status === "pending") {
              const config = dataSyncManagerConfig.find(c => c?.indexDb === entry?.name);
              if (!config) continue;

              const featureData = updatedFeatures.find((f: any) => f?.name === config?.model);
              
              if (featureData) {
                // Clear and update the model's data
                await clearAllDataFromLocalDb(config.indexDb);
                
                entry.status = "success";
                entry.category = featureData.category || "";
              } else {
                entry.status = "error";
              }
            }
          }

          // Update DataSyncManager with new sync information
          await clearAllDataFromLocalDb("DataSyncManager");
          await insertDataIntoLocalDB("DataSyncManager", updatedDataSyncManager);
          
          localStorage.setItem("lastDataSyncTime", new Date().toISOString());

        } catch (error) {
          console.error("Incremental sync failed:", error);
          
          // Mark pending entries as error
          const errorUpdatedEntries = updatedDataSyncManager.map(entry => 
            entry.status === "pending" ? { ...entry, status: "error" as const } : entry
          );
          
          await clearAllDataFromLocalDb("DataSyncManager");
          await insertDataIntoLocalDB("DataSyncManager", errorUpdatedEntries);
        }
      };

      dataSync();
      const intervalId = setInterval(() => {
        dataSync();
      }, 60000);

      // Cleanup on unmount
      return () => clearInterval(intervalId);
    }, []);

    async function checkParamsToken() {
        // let lob = window?.location?.hostname?.split(".")?.[0] ?? null;
        let lob = getLob();
        if (!lob) return false; // false means error
        localStorage.setItem("lob", lob);
        try {
            const user = await getUserFromToken(token!);
            setAuthContext(user, token!);
            return true; // true means success
        } catch (error: any) {
            console.error(error);
            return false;
        }
    }
    const getAndSetAllowedFeatures = useCallback(async () => {
        setLoadChildRoute(false);
        let isSuccessInSsoTokenAuth = true;
        if (token && token !== "") {
            isSuccessInSsoTokenAuth = await checkParamsToken();
        }
        if(error){
            const message = error?.split(":")?.[0] ?? "Authorization Failed";
            openPopup("Error",message , async ()=>{
                logoutUser();
            });
            return;
        }
       const isVerifiedUser = token ? isSuccessInSsoTokenAuth : await verifyAndRefreshUserToken();

        if (isVerifiedUser) {
            let portalConfig;
            if (clientConfigRef.current && clientConfigRef.current.length > 0) {
                portalConfig = clientConfigRef.current?.find((config:any)=>config.domainType === "portal_configuration");
            } else {
                portalConfig = await getMetaDataConfig("clientconfig", "portal_configuration")
            }
            if (portalConfig && isStandardLob(portalConfig) && !(isProductionMovementCompleted(portalConfig))) {
                if (!onboardingDataRef.current) {
                    const onboardingConfig = await getOnboardingConfig();
                    onboardingDataRef.current = onboardingConfig;
                } 
                if (onboardingDataRef.current && onboardingDataRef.current.isOnboarding) {
                    onboardingDataRef.current = undefined;
                    return navigate('/onboarding');
                }
            }
            let storedPortalConfig = store.getState().portalConfigState;
            if (!storedPortalConfig) {
                const newPortalConfig = await getPortalRoleConfig();
                setPortalConfig(newPortalConfig);
                storedPortalConfig = newPortalConfig;
            }
            const { featureConfig } = storedPortalConfig;
            if (featureConfig.length !== 0) {
                const allAccessibleRoutes = getAllAccessibleRoutes(featureConfig);
                const curRouteOption = allAccessibleRoutes.find((accessibleRoute) => {
                    return currentRoute.includes(accessibleRoute);
                })
                if (!curRouteOption) {
                    const specialAllowedRoutes: string[] = ['/dashboard/', '/dashboard/adminPanel', "/dashboard/couponDashboard/couponData", "/dashboard/changePassword", "/dashboard/revert","/dashboard/scaiDashboard" , "/dashboard/settings","/dashboard/settings/profile","/dashboard/settings/feature-management","/dashboard/settings/application-details","/dashboard/settings/backup-restore","/dashboard/profile","/manageInvoice"] //adminPanel just To Show available adminOptions only, /dashboard/couponDashboard/couponData to allow access to couponDashboard and couponData
                    const isNotSpecialAllowedRoute = specialAllowedRoutes.filter((specialRoute) => specialRoute.includes(currentRoute)).length === 0;
                    if (isNotSpecialAllowedRoute) {
                        openPopup("Alert", "You don't have the access to this page", () => {
                            navigate('/dashboard');
                        });
                        return;
                    }
                }
                let runPrepareCatalogue =  sessionStorage.getItem('runPrepareCatalogue') ?? false;
                const channelDivision = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user?.channelDivision;
                const dms = await fetchDMSConfig(clientConfigRef.current);
                
                // Initialize push notifications after login
                initializePushNotifications(
                  async (fcmToken) => {
                    try {
                      await ChannelkartNetworkPut("/v1/usercontext", {
                        usercontext: fcmToken,
                      });
                    } catch (error) {
                      console.error("Error sending FCM token to backend:", error);
                    }
                  },
                  (payload) => {
                    console.log("Foreground message received:", payload);
                  }
                );
                
                //for cokearg, if not dms then also fetch and store client config
                const fetchClientConfig = await fetchPortalClientConfig(clientConfigRef.current)
                if (fetchClientConfig) await fetchAndStoreClientConfig()
                if (runPrepareCatalogue && dms) {
                  const products = await fetchProducts();
                  await insertDataIntoLocalDB("Product", [{ id: 1, data: products }]);

                  try {
                    await fetchAndStoreClientConfig();

                    //clear loadout & dayend configs from db on login
                    const existingCachedDayEndData = getDayEndStatusFromSession();
                    clearAllDataFromLocalDb("Cart");
                    if (existingCachedDayEndData !== null){
                      await completeDayEnd();
                    }

                    await setDayEndStatusInSession();
                    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
                    if(ConfigDictionary?.showRouteNameId){
                       const routeIdNameMapping = await fetchRouteIdNameMapping(JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user?.loginId);
                       await insertDataIntoLocalDB("RouteNameIdMapping", [{ id: 1, data: routeIdNameMapping }]);
                    }

                    const skuReplacementConfig = await getConfigData("dms_configuration", "enableSKUReplacement");
                    const skuReplacementConfigValue = skuReplacementConfig[0]?.configValue ?? "";
      
                    if(skuReplacementConfigValue) {
                        const results = await fetchSkuReplacementMapping()
                        await insertDataIntoLocalDB("SkuReplacementData", [{ id: 1, data: results }]);
                    }
                    if(ConfigDictionary?.showSiteWarehouseFilterViewPrimaryOMS) {
                      const results = await fetchSiteWarehouseMapping()
                      await insertDataIntoLocalDB("SiteWarehouseIdMapping", [{ id: 1, data: results }]);
                    }    
  
                    if(channelDivision) {
                        const disableStockCheckConfig = await getConfigData(
                          "dms_configuration",
                          "disableStockCheck"
                        );
                        const disableStockCheck = disableStockCheckConfig[0]?.configValue ?? false;
                        if (!disableStockCheck) {
                          try {
                            await setRealTimeStockDataInDb();
                          } catch (error) {
                            console.log(error);
                          }
                        }
                        let fetchPricing = await fetchPricingAtLogin();

                        await clearAllDataFromLocalDb("DataSyncManager"); // clear entire DataSyncManager
                        if(fetchPricing) {
                          await prepareCatalogue();
                        }
                    }
                    sessionStorage.removeItem("runPrepareCatalogue");
                  } catch (error) {
                    console.log(error);
                  }
                }
                const saleslensredirect = await fetchSaleLensConfig(clientConfigRef.current);
                if(saleslensredirect){
                  const salesLensUrl=await getSalesLensUrl()
                  console.log("salesLensUrl",salesLensUrl)
                  if(salesLensUrl){
                      window.location.href = salesLensUrl;
                      return
                  }else{
                    await logoutUser();
                  }
                }else{
                setLoadChildRoute(true);
                }
            } 
            else {
                // restrict user by showing some error
                openPopup("Alert", "You don't have access to portal", async () => {
                    logoutUser();
                })
            }
        } else {
            if(token && token !== "")
                openPopup("Error", "Authorization Failed", async() => {
                    logoutUser();
                });
            else 
                logoutUser();
        }
    }, [currentRoute]);

    // useEffect(() => {
    //     if (showProductionMovementSuccessPopup) {
    //         openPopup("Success", "You have completed the \"Move to Production\" journey.\nWelcome to the Salescode Studio Portal!");
    //     }
    // }, [showProductionMovementSuccessPopup]);

    useEffect(() => {
        getAndSetAllowedFeatures();
    },[getAndSetAllowedFeatures])

    useEffect(() => {
        async function getAndSetClientConfig(){
            const clientConfig = await getMetaDataConfig("clientconfig");
            clientConfigRef.current = clientConfig;
        }
        getAndSetClientConfig()
    },[])
    useEffect(() => {
        const currentUserData = JSON.parse(localStorage.getItem("authContext") ?? "{}")?.user;
        const isAdmin = currentUserData?.designation?.includes("admin");
        const isReportAdmin = currentUserData?.designation?.includes("reportadmin");
        if(isAdmin || isReportAdmin){
            doFinalConfigSubmit("playground_config", []);
        }
    })

    return (
        <>
            {
                loadChildRoute? <Outlet/> : <Loader />
            }
            <ProductionMovementFloaterIcon isOnboarding={onboardingDataRef.current?.isOnboarding || false} productionMovementStateChange={showProductionMovementSuccessPopup} />
        </>
    );
}
